import { ThemeEvents } from '@theme/events';

class CustomSubscription extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;
    this.handleChange = this.handleSelectionChange.bind(this);
    this.handleVariantUpdate = this.onVariantUpdate.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.init();
    this.attachVariantListener();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.eventTarget?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.handleVariantUpdate
    );
  }

  init() {
    this.form = this.findForm();
    if (!this.form) return;

    if (!this.form.getAttribute('id')) {
      this.form.setAttribute(
        'id',
        `product-form-${Math.random().toString(36).slice(2, 9)}`
      );
    }

    this.sellingPlanIdInput = this.querySelector('input[name="selling_plan"]');
    if (!this.sellingPlanIdInput) return;

    this.sellingPlanIdInput.setAttribute('form', this.form.getAttribute('id'));
    this.variantWrappers = Array.from(
      this.querySelectorAll('.custom-subscription-variant')
    );

    this.addEventListener('change', this.handleChange);

    const initialVariantId =
      this.form.querySelector('input[name="id"]')?.value ||
      this.form.querySelector('select[name="id"]')?.value;

    if (initialVariantId) {
      this.setActiveWrapper(initialVariantId);
    }

    this.syncVisibility();
    this.syncSelectionFromActiveWrapper();
    this.syncWidgetPriceFromSelection();
    this.syncAtcPriceFromSelection();
  }

  findForm() {
    const directForm = this.closest('product-form-component')?.querySelector('form');
    if (directForm) return directForm;

    const scopes = [
      this.closest('.product-details'),
      this.closest('dialog'),
      this.closest('product-card'),
      this.closest('.shopify-section'),
    ].filter(Boolean);

    for (const scope of scopes) {
      const form = scope.querySelector('product-form-component form');
      if (form) return form;
    }

    return null;
  }

  attachVariantListener() {
    if (!this.sellingPlanIdInput) return;

    this.eventTarget =
      this.closest('dialog') ||
      this.closest('product-card') ||
      this.closest('.shopify-section') ||
      this.closest('.product-details');

    this.eventTarget?.addEventListener(
      ThemeEvents.variantUpdate,
      this.handleVariantUpdate
    );
  }

  getActiveWrapper() {
    return this.querySelector('.custom-subscription-variant.active');
  }

  setActiveWrapper(variantId) {
    let activeWrapper = null;

    this.variantWrappers.forEach((wrapper) => {
      const isActive = String(wrapper.dataset.id) === String(variantId);
      wrapper.classList.toggle('active', isActive);

      if (isActive) {
        activeWrapper = wrapper;
      }
    });

    return activeWrapper;
  }

  syncVisibility() {
    this.hidden = !this.getActiveWrapper();
  }

  syncSelectionFromActiveWrapper() {
    if (!this.sellingPlanIdInput) return;

    const activeWrapper = this.getActiveWrapper();
    if (!activeWrapper) {
      this.sellingPlanIdInput.value = '';
      return;
    }

    let selectedOptionEl = activeWrapper.querySelector(
      '.custom-subscription__option_input:checked'
    );

    if (!selectedOptionEl) {
      selectedOptionEl =
        activeWrapper.querySelector('.custom-subscription__option_input[data-default="true"]') ||
        activeWrapper.querySelector('.custom-subscription__option_input');

      if (selectedOptionEl) {
        selectedOptionEl.checked = true;
      }
    }

    if (!selectedOptionEl || selectedOptionEl.value === 'onetime') {
      this.sellingPlanIdInput.value = '';
      return;
    }

    let selectedFrequencyEl = activeWrapper.querySelector(
      '.custom-subscription__frequency-input:checked'
    );

    if (!selectedFrequencyEl) {
      selectedFrequencyEl =
        activeWrapper.querySelector('.custom-subscription__frequency-input[data-default="true"]') ||
        activeWrapper.querySelector('.custom-subscription__frequency-input');

      if (selectedFrequencyEl) {
        selectedFrequencyEl.checked = true;
      }
    }

    this.sellingPlanIdInput.value = selectedFrequencyEl?.value || '';

    if (selectedFrequencyEl) {
      selectedOptionEl.dataset.price = selectedFrequencyEl.dataset.price || '';

      if (selectedFrequencyEl.dataset.comparePrice) {
        selectedOptionEl.dataset.comparePrice = selectedFrequencyEl.dataset.comparePrice;
      } else {
        delete selectedOptionEl.dataset.comparePrice;
      }
    }
  }

  getAtcPriceElements() {
    const addToCartButton = this.form?.querySelector('[ref="addToCartButton"]');

    return {
      current: addToCartButton?.querySelector('.add-to-cart-price__current'),
      compare: addToCartButton?.querySelector('.add-to-cart-price__compare'),
    };
  }

  getWidgetPriceElements(activeWrapper) {
    const subscribeLabel = activeWrapper?.querySelector('.custom-subscription__subscribe');

    return {
      current: subscribeLabel?.querySelector('.custom-subscription__final-price'),
      compare: subscribeLabel?.querySelector('.custom-subscription__original-price'),
    };
  }

  syncWidgetPriceFromSelection() {
    const activeWrapper = this.getActiveWrapper();
    if (!activeWrapper) return;

    const subscribeOptionEl = activeWrapper.querySelector(
      '.custom-subscription__option_input[value="subscribe"]'
    );
    const selectedFrequencyEl = activeWrapper.querySelector(
      '.custom-subscription__frequency-input:checked'
    );

    if (!subscribeOptionEl || !selectedFrequencyEl) return;

    const currentPrice = selectedFrequencyEl.dataset.price || subscribeOptionEl.dataset.price || '';
    const comparePrice =
      selectedFrequencyEl.dataset.comparePrice || subscribeOptionEl.dataset.comparePrice || '';

    const { current, compare } = this.getWidgetPriceElements(activeWrapper);

    if (current && currentPrice) {
      current.textContent = currentPrice;
    }

    if (compare) {
      if (comparePrice) {
        compare.textContent = comparePrice;
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }
    }
  }

  syncAtcPriceFromSelection() {
    const activeWrapper = this.getActiveWrapper();
    if (!activeWrapper || this.hidden) return;

    const selectedOptionEl = activeWrapper.querySelector(
      '.custom-subscription__option_input:checked'
    );
    const selectedFrequencyEl = activeWrapper.querySelector(
      '.custom-subscription__frequency-input:checked'
    );

    if (!selectedOptionEl) return;

    let price = selectedOptionEl.dataset.price || '';
    let comparePrice = selectedOptionEl.dataset.comparePrice || '';

    if (selectedOptionEl.value === 'subscribe' && selectedFrequencyEl) {
      price = selectedFrequencyEl.dataset.price || price;
      comparePrice = selectedFrequencyEl.dataset.comparePrice || comparePrice;
    }

    const { current, compare } = this.getAtcPriceElements();

    if (current && price) {
      current.textContent = price;
    }

    if (compare) {
      if (comparePrice) {
        compare.textContent = comparePrice;
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }
    }
  }

  handleSelectionChange() {
    this.syncSelectionFromActiveWrapper();
    this.syncWidgetPriceFromSelection();
    this.syncAtcPriceFromSelection();
  }

  onVariantUpdate(event) {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    this.setActiveWrapper(variantId);
    this.syncVisibility();
    this.syncSelectionFromActiveWrapper();
    this.syncWidgetPriceFromSelection();
    this.syncAtcPriceFromSelection();
  }
}

if (!customElements.get('custom-subscription')) {
  customElements.define('custom-subscription', CustomSubscription);
}
